import { Download, ListFilter } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type { ProductPerformance, ProductType } from '@/types/analytics'

type ProductFilter = 'all' | ProductType

const filterLabels: Record<ProductFilter, string> = {
  all: 'All Products',
  recommended: 'Recommended',
  welcome: 'Welcome Products',
  fallback: 'Fallback Products',
  pinned: 'Pinned Products',
}

const filterSubtitles: Record<ProductFilter, string> = {
  all: 'Product performance by views and clicks',
  recommended: 'AI-recommended product performance',
  welcome: 'Welcome product performance',
  fallback: 'Fallback product performance',
  pinned: 'Pinned product performance',
}

interface TopProductsTableProps {
  data: ProductPerformance[]
  onExport?: () => void
  isExporting?: boolean
}

const tableClassNames = {
  card: 'h-full flex flex-col rounded-2xl shadow-sm',
  header: 'pb-2 shrink-0',
  title: 'text-base font-semibold leading-4',
  subtitle: 'text-sm text-muted-foreground',
  content: 'pt-0 overflow-y-auto flex-1 min-h-0',
  table: 'w-full table-fixed',
  thead: 'text-left text-sm text-muted-foreground',
  th: 'pb-3 font-medium',
  thNumeric: 'pb-3 font-medium text-right w-20',
  tbody: 'text-sm',
  tr: 'border-t border-border',
  td: 'py-3',
  tdProduct: 'py-3 max-w-0',
  tdNumeric: 'py-3 text-right tabular-nums w-20',
  productName: 'font-medium text-foreground block truncate cursor-default',
}

export function TopProductsTable({
  data,
  onExport,
  isExporting = false,
}: TopProductsTableProps) {
  const [filter, setFilter] = useState<ProductFilter>('all')

  const filteredData = useMemo(() => {
    if (filter === 'all') {
      return data
    }
    return data.filter((product) => product.productType === filter)
  }, [data, filter])

  const filters: ProductFilter[] = [
    'all',
    'recommended',
    'welcome',
    'fallback',
    'pinned',
  ]

  return (
    <Card className={tableClassNames.card}>
      <CardHeader className={tableClassNames.header}>
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-1.5">
            <CardTitle className={tableClassNames.title}>
              Top Products
            </CardTitle>
            <p className={tableClassNames.subtitle}>
              {filterSubtitles[filter]}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 rounded-full"
                >
                  <ListFilter className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {filters.map((f) => (
                  <DropdownMenuCheckboxItem
                    key={f}
                    checked={filter === f}
                    onCheckedChange={() => setFilter(f)}
                  >
                    {filterLabels[f]}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {onExport && (
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 rounded-full"
                onClick={onExport}
                disabled={isExporting}
                aria-label="Export CSV"
              >
                <Download className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className={tableClassNames.content}>
        <TooltipProvider delayDuration={300}>
          <table className={tableClassNames.table}>
            <thead className={tableClassNames.thead}>
              <tr>
                <th className={tableClassNames.th}>Product</th>
                <th className={tableClassNames.thNumeric}>Views</th>
                <th className={tableClassNames.thNumeric}>Clicks</th>
                <th className={tableClassNames.thNumeric}>CTR %</th>
              </tr>
            </thead>
            <tbody className={tableClassNames.tbody}>
              {filteredData.map((product) => {
                const displayName = product.productName || product.productId
                // Create unique key combining productId and productType
                // since same product could appear in both welcome and recommended
                const uniqueKey = `${product.productId}-${product.productType}`
                return (
                  <tr key={uniqueKey} className={tableClassNames.tr}>
                    <td className={tableClassNames.tdProduct}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className={tableClassNames.productName}>
                            {displayName}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" align="start">
                          <p className="max-w-xs">{displayName}</p>
                        </TooltipContent>
                      </Tooltip>
                    </td>
                    <td className={tableClassNames.tdNumeric}>
                      {product.viewCount.toLocaleString()}
                    </td>
                    <td className={tableClassNames.tdNumeric}>
                      {product.clickCount.toLocaleString()}
                    </td>
                    <td className={tableClassNames.tdNumeric}>
                      {product.ctr.toFixed(1)}%
                    </td>
                  </tr>
                )
              })}
              {filteredData.length === 0 && (
                <tr className={tableClassNames.tr}>
                  <td
                    colSpan={4}
                    className="py-6 text-center text-muted-foreground"
                  >
                    No product data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </TooltipProvider>
      </CardContent>
    </Card>
  )
}
